import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  MenuItem,
  TextField,
  FormControl,
  Grid,
  Modal,
  IconButton,
  Chip,
  Link as MuiLink,
  useTheme,
  alpha,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PaymentIcon from "@mui/icons-material/Payment";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { motion } from "framer-motion";
import proxy from "../api/axios";
import {
  MOCK_INTERVIEW_BOOKING_PRICE,
  MOCK_INTERVIEW_PREMIUM_PRICE,
  getMockInterviewPrice,
  GUIDANCE_SESSION_PRICE,
  MENTORSHIP_MONTHLY_PRICE,
  MENTORSHIP_SIX_MONTH_PRICE,
  interviewTimeSlots,
  PAYMENT_LIVE_KEY,
} from "../common/constants";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";
import { Helmet } from "react-helmet";
import TestimonialsSection from "../components/TestimonialsSection";

const ACCENT = "#137274";
const PINK = "#f50057";

const formatInr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const whyUs = [
  {
    icon: <WorkspacePremiumOutlinedIcon sx={{ fontSize: 22 }} />,
    title: "FAANG SDE mentor",
    body: "Learn how top companies actually interview — from someone who’s lived those loops.",
  },
  {
    icon: <CodeOutlinedIcon sx={{ fontSize: 22 }} />,
    title: "DSA specialist",
    body: "Patterns, complexity, and communication drilled until they stick under pressure.",
  },
  {
    icon: <ArchitectureOutlinedIcon sx={{ fontSize: 22 }} />,
    title: "System Design + LLD",
    body: "Capacity estimates, APIs, trade-offs, and design reviews at industry bar.",
  },
  {
    icon: <MemoryOutlinedIcon sx={{ fontSize: 22 }} />,
    title: "CS Fundamentals",
    body: "OS, networks, databases, and concurrency — the questions that separate offers.",
  },
];

const included = [
  {
    icon: <ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />,
    title: "Daily connect",
    body: "Stay accountable with regular check-ins — not a once-a-month dump of advice.",
  },
  {
    icon: <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 20 }} />,
    title: "Follow-ups that stick",
    body: "Action items after every session, reviewed next time so progress compounds.",
  },
  {
    icon: <ForumOutlinedIcon sx={{ fontSize: 20 }} />,
    title: "Mock interview loops",
    body: "Timed DSA / design / behavioral rounds with structured, written feedback.",
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
    title: "Personalized roadmap",
    body: "DSA + System Design + CS Fundamentals sequenced to your target companies.",
  },
  {
    icon: <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />,
    title: "Resume & story craft",
    body: "Impact bullets, project narratives, and “tell me about yourself” that land.",
  },
  {
    icon: <RocketLaunchOutlinedIcon sx={{ fontSize: 20 }} />,
    title: "Referrals (not placements)",
    body: "2–3 FAANG + 5–6 product-company referrals when you’re ready — jobs are never guaranteed.",
  },
];

/** 6–7 month mentorship planner */
const plannerPhases = [
  {
    phase: "Months 1–4",
    duration: "4 months",
    title: "DSA depth",
    accent: ACCENT,
    points: [
      "Patterns, complexity, and communication under timed pressure",
      "Level 1 → Level 2: arrays → graphs, DP, and hard patterns",
      "Weekly mocks + written feedback; daily connect for accountability",
      "Company-style follow-ups until patterns stick",
    ],
  },
  {
    phase: "Months 5–6",
    duration: "2 months",
    title: "HLD · System Design",
    accent: "#2563eb",
    points: [
      "Capacity estimates, APIs, storage, caching, consistency trade-offs",
      "End-to-end designs: feeds, chat, payments, rate limiters, and more",
      "Whiteboard / doc-style reviews at FAANG bar",
      "Mock HLD loops with structured critique",
    ],
  },
  {
    phase: "Month 6–7",
    duration: "~15 days",
    title: "LLD polish",
    accent: "#d97706",
    points: [
      "OOP, SOLID, class design, and common LLD machines",
      "Parking lot, BookMyShow, elevators, and similar classics",
      "Clean interfaces and extensibility interviewers look for",
    ],
  },
  {
    phase: "Month 6–7",
    duration: "~15 days",
    title: "CS Fundamentals",
    accent: "#7c3aed",
    points: [
      "OS, networks, databases, concurrency, and memory basics",
      "Questions that separate strong candidates in product loops",
      "Quick revision sheets + targeted Q&A",
    ],
  },
];

const careerPrep = [
  {
    title: "Resume writing",
    body: "Rewrite bullets for impact, metrics, and ownership — not task lists. ATS-friendly structure for FAANG and product companies.",
  },
  {
    title: "Interview storytelling",
    body: "Projects, conflicts, leadership, and failure stories drilled with STAR — ready for behavioral and HM rounds.",
  },
  {
    title: "Offer-season prep",
    body: "Company research, loop strategy, and negotiation framing when offers start landing.",
  },
];

const BookingPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const location = useLocation();
  const isMockPath = location.pathname.startsWith("/mock-interview");
  const isPremiumUser = Boolean(
    useSelector((state) => state.user.userInfo?.isPremiumUser),
  );
  const mockInterviewPrice = getMockInterviewPrice(isPremiumUser);

  const [plan, setPlan] = useState("mock"); // mock | monthly | six
  const [interviewType, setInterviewType] = useState("Mock Interview · DSA");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(dayjs());
  const [timeSlot, setTimeSlot] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [failedPaymentModalOpen, setFailedPaymentModalOpen] = useState(false);

  useEffect(() => {
    if (!isMockPath) return;
    const t = window.setTimeout(() => {
      document.getElementById("mock-interview")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);
    return () => window.clearTimeout(t);
  }, [isMockPath]);

  const border = isDark ? "#30363d" : "rgba(15, 60, 70, 0.1)";
  const surface = isDark ? "#161b22" : "#ffffff";
  const muted = isDark ? "#8b949e" : "#5f6b73";
  const pageBg = isDark
    ? "#0f1419"
    : "linear-gradient(180deg, #eef6f8 0%, #f7f9fb 40%, #ffffff 100%)";

  const amount =
    plan === "monthly"
      ? MENTORSHIP_MONTHLY_PRICE
      : plan === "six"
        ? MENTORSHIP_SIX_MONTH_PRICE
        : plan === "guidance"
          ? GUIDANCE_SESSION_PRICE
          : mockInterviewPrice;

  const isGuidance = plan === "guidance";
  const isMock = plan === "mock";
  /** Guidance + mock both need a calendar slot. */
  const needsSlot = isGuidance || isMock;
  const planLabel =
    plan === "monthly"
      ? "1:1 Mentorship · Monthly"
      : plan === "six"
        ? "1:1 Mentorship · 6–7 month plan"
        : plan === "guidance"
          ? "1–2 hour guidance call"
          : "Mock Interview";

  const openCheckout = (nextPlan) => {
    setPlan(nextPlan);
    setTimeSlot("");
    if (nextPlan === "guidance") {
      setInterviewType("Guidance · DSA / Design / CS");
    } else if (nextPlan === "mock") {
      setInterviewType("Mock Interview · DSA");
    } else {
      setInterviewType("Complete Interview Prep Mentorship");
    }
    setModalOpen(true);
  };

  const handleBooking = async () => {
    setLoading(true);
    try {
      const booking = {
        name,
        email,
        interviewType,
        plan,
        planLabel,
        date: needsSlot ? date : null,
        timeSlot: needsSlot ? timeSlot : "Onboarding call (we’ll schedule)",
        contactNumber,
      };
      const orderResponse = await proxy.post("/payments/create-order", {
        currency: "INR",
        booking,
      });
      const { orderId, amount: chargedAmount } = orderResponse.data;
      const checkoutAmount = chargedAmount ?? amount;

      const options = {
        key: PAYMENT_LIVE_KEY,
        amount: checkoutAmount * 100,
        currency: "INR",
        name: "CrafterHack",
        description: planLabel,
        order_id: orderId,
        handler: async (response) => {
          const { razorpay_order_id, razorpay_payment_id, razorpay_signature } =
            response;

          const verifyResponse = await proxy.post("/payments/verify-payment", {
            razorpay_order_id,
            razorpay_payment_id,
            razorpay_signature,
            amount: checkoutAmount,
          });

          if (verifyResponse.data.status === "success") {
            setModalOpen(false);
            setConfirmationModalOpen(true);
          } else {
            setFailedPaymentModalOpen(true);
          }
        },
        prefill: {
          name,
          email,
          contact: contactNumber,
        },
        theme: { color: ACCENT },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("Payment Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const canPay =
    Boolean(name && email && contactNumber) &&
    (needsSlot ? Boolean(timeSlot) : true) &&
    !loading;

  const mockPriceLabel = isPremiumUser ? (
    <>
      {formatInr(MOCK_INTERVIEW_PREMIUM_PRICE)}
      <Box
        component="span"
        sx={{
          textDecoration: "line-through",
          color: muted,
          ml: 0.75,
          fontSize: "0.85em",
          fontWeight: 600,
        }}
      >
        {formatInr(MOCK_INTERVIEW_BOOKING_PRICE)}
      </Box>
    </>
  ) : (
    formatInr(MOCK_INTERVIEW_BOOKING_PRICE)
  );

  return (
    <Box sx={{ minHeight: "100vh", background: pageBg }}>
      <Helmet>
        <title>
          {isMockPath
            ? `Mock Interview · ${isPremiumUser ? "₹1" : "₹99"} with FAANG SDE | CrafterHack`
            : "1:1 Mentorship · FAANG SDE Interview Prep | CrafterHack"}
        </title>
        <meta
          name="description"
          content={
            isMockPath
              ? `Book a ${isPremiumUser ? "₹1 premium" : "₹99"} mock interview with a FAANG SDE — DSA, System Design, LLD, or behavioral. Instant scheduling on CrafterHack.`
              : "1:1 mentorship with a FAANG SDE — 6–7 month planner: 4 months DSA, 2 months HLD, LLD + CS fundamentals. Resume prep. Referrals included (not job guarantees). ₹11,999/mo or ₹59,999 plan. 1–2 hour guidance from ₹1,199. Mock interviews from ₹99 (₹1 for premium)."
          }
        />
        <link
          rel="canonical"
          href={`https://crafterhack.com${isMockPath ? "/mock-interview" : "/mentorship"}`}
        />
      </Helmet>

      {/* Hero — full bleed */}
      <Box
        sx={{
          position: "relative",
          px: { xs: 2, sm: 3 },
          pt: { xs: 4, md: 6 },
          pb: { xs: 5, md: 7 },
          borderBottom: `1px solid ${border}`,
          overflow: "hidden",
          background: isDark
            ? `radial-gradient(900px 420px at 12% -10%, ${alpha(ACCENT, 0.4)}, transparent 55%), radial-gradient(700px 360px at 95% 10%, ${alpha(PINK, 0.14)}, transparent 50%), #0f1419`
            : `radial-gradient(900px 420px at 10% -20%, ${alpha(ACCENT, 0.2)}, transparent 55%), radial-gradient(700px 360px at 95% 0%, ${alpha(PINK, 0.1)}, transparent 50%), linear-gradient(180deg, #e8f4f5 0%, #f7f9fb 100%)`,
        }}
      >
        <Box
          sx={{ maxWidth: 1040, mx: "auto", position: "relative", zIndex: 1 }}
        >
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <Chip
              icon={
                <WorkspacePremiumOutlinedIcon
                  sx={{ fontSize: "16px !important" }}
                />
              }
              label="FAANG SDE · DSA & System Design expert"
              sx={{
                mb: 2,
                fontWeight: 800,
                bgcolor: alpha(ACCENT, isDark ? 0.28 : 0.14),
                color: ACCENT,
                "& .MuiChip-icon": { color: ACCENT },
              }}
            />
            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: "2rem", sm: "2.65rem", md: "3rem" },
                letterSpacing: "-0.04em",
                lineHeight: 1.08,
                mb: 1.5,
                maxWidth: 720,
              }}
            >
              CrafterHack{" "}
              <Box component="span" sx={{ color: ACCENT }}>
                Mentorship
              </Box>
            </Typography>
            <Typography
              sx={{
                color: muted,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.55,
                maxWidth: 560,
                mb: 2.5,
              }}
            >
              Complete interview prep on a 6–7 month planner — 4 months DSA, 2
              months HLD, then LLD and CS fundamentals — with daily connect,
              resume guidance, and referrals. Or book a focused 1–2 hour
              guidance call.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 2.5 }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => openCheckout("six")}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  bgcolor: ACCENT,
                  px: 2.5,
                  "&:hover": { bgcolor: "#0f5c5e" },
                }}
              >
                Start mentorship
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => openCheckout("guidance")}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderColor: border,
                  color: "inherit",
                  bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#fff", 0.7),
                }}
              >
                Book guidance · {formatInr(GUIDANCE_SESSION_PRICE)}
              </Button>
              <Button
                variant="text"
                size="large"
                onClick={() => openCheckout("mock")}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: PINK,
                }}
              >
                Mock · {mockPriceLabel}
              </Button>
            </Box>
            <Typography
              variant="caption"
              sx={{ color: muted, fontWeight: 700 }}
            >
              {formatInr(MENTORSHIP_MONTHLY_PRICE)}/mo ·{" "}
              {formatInr(MENTORSHIP_SIX_MONTH_PRICE)} for 6–7 months (best
              value) · guidance {formatInr(GUIDANCE_SESSION_PRICE)}
            </Typography>
          </motion.div>
        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: 1040,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          py: { xs: 4, md: 5 },
        }}
      >
        {/* Why us */}
        <Box sx={{ mb: 5 }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: "1.35rem", sm: "1.6rem" },
              letterSpacing: "-0.03em",
              mb: 0.75,
            }}
          >
            Why engineers choose us
          </Typography>
          <Typography
            sx={{ color: muted, mb: 2.5, maxWidth: 520, fontSize: "0.95rem" }}
          >
            Industry bar — not generic coaching. Built for engineers targeting
            product companies.
          </Typography>
          <Grid container spacing={1.75}>
            {whyUs.map((item) => (
              <Grid item xs={12} sm={6} key={item.title}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    p: 2,
                    height: "100%",
                    borderRadius: 2,
                    border: `1px solid ${border}`,
                    bgcolor: surface,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1),
                      color: ACCENT,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, mb: 0.35 }}>
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: muted,
                        fontSize: "0.85rem",
                        lineHeight: 1.45,
                      }}
                    >
                      {item.body}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* What's included */}
        <Box sx={{ mb: 5 }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: "1.35rem", sm: "1.6rem" },
              letterSpacing: "-0.03em",
              mb: 0.75,
            }}
          >
            Complete interview prep — what’s inside
          </Typography>
          <Typography
            sx={{ color: muted, mb: 2.5, maxWidth: 540, fontSize: "0.95rem" }}
          >
            One program covering DSA + System Design + CS Fundamentals, with
            accountability baked in.
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr 1fr",
              },
              gap: 1.5,
            }}
          >
            {included.map((item) => (
              <Box
                key={item.title}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${border}`,
                  bgcolor: isDark ? alpha("#fff", 0.02) : alpha(ACCENT, 0.03),
                }}
              >
                <Box sx={{ color: ACCENT, mb: 1, display: "flex" }}>
                  {item.icon}
                </Box>
                <Typography
                  sx={{ fontWeight: 800, fontSize: "0.95rem", mb: 0.4 }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: muted, fontSize: "0.82rem", lineHeight: 1.45 }}
                >
                  {item.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* 6–7 month planner */}
        <Box sx={{ mb: 5 }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: "1.35rem", sm: "1.6rem" },
              letterSpacing: "-0.03em",
              mb: 0.75,
            }}
          >
            6–7 month planner
          </Typography>
          <Typography
            sx={{
              color: muted,
              mb: 2.5,
              maxWidth: 580,
              fontSize: "0.95rem",
              lineHeight: 1.55,
            }}
          >
            A clear runway from DSA foundations to design depth — then LLD, CS
            fundamentals, resume polish, and referral season. Built for the{" "}
            {formatInr(MENTORSHIP_SIX_MONTH_PRICE)} plan (also available
            month-to-month).
          </Typography>

          {/* Timeline bar */}
          <Box
            sx={{
              display: "flex",
              height: 10,
              borderRadius: 99,
              overflow: "hidden",
              mb: 1.25,
              border: `1px solid ${border}`,
            }}
          >
            <Box sx={{ flex: 4, bgcolor: ACCENT }} title="DSA 4 months" />
            <Box sx={{ flex: 2, bgcolor: "#2563eb" }} title="HLD 2 months" />
            <Box sx={{ flex: 0.5, bgcolor: "#d97706" }} title="LLD ~15 days" />
            <Box sx={{ flex: 0.5, bgcolor: "#7c3aed" }} title="CS ~15 days" />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 2.75,
            }}
          >
            {[
              { label: "4 mo DSA", color: ACCENT },
              { label: "2 mo HLD", color: "#2563eb" },
              { label: "15d LLD", color: "#d97706" },
              { label: "15d CS", color: "#7c3aed" },
            ].map((t) => (
              <Box
                key={t.label}
                sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 0.5,
                    bgcolor: t.color,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: muted }}
                >
                  {t.label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Grid container spacing={1.75}>
            {plannerPhases.map((p) => (
              <Grid item xs={12} sm={6} key={`${p.phase}-${p.title}`}>
                <Box
                  sx={{
                    height: "100%",
                    p: 2.25,
                    borderRadius: 2.5,
                    border: `1px solid ${border}`,
                    borderTop: `3px solid ${p.accent}`,
                    bgcolor: surface,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip
                      size="small"
                      label={p.phase}
                      sx={{
                        height: 22,
                        fontWeight: 800,
                        fontSize: "0.65rem",
                        bgcolor: alpha(p.accent, isDark ? 0.25 : 0.12),
                        color: p.accent,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: muted }}
                    >
                      {p.duration}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.05rem",
                      mb: 1.25,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {p.title}
                  </Typography>
                  {p.points.map((pt) => (
                    <Box
                      key={pt}
                      sx={{
                        display: "flex",
                        gap: 1,
                        mb: 0.75,
                        alignItems: "flex-start",
                      }}
                    >
                      <CheckCircleIcon
                        sx={{
                          fontSize: 15,
                          color: p.accent,
                          mt: 0.25,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: muted,
                          fontSize: "0.82rem",
                          lineHeight: 1.45,
                        }}
                      >
                        {pt}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Resume & prep */}
          <Box sx={{ mt: 2.5 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "1.05rem",
                mb: 1.5,
                letterSpacing: "-0.02em",
              }}
            >
              Resume writing & preparation guidance
            </Typography>
            <Grid container spacing={1.5}>
              {careerPrep.map((item) => (
                <Grid item xs={12} md={4} key={item.title}>
                  <Box
                    sx={{
                      p: 2,
                      height: "100%",
                      borderRadius: 2,
                      border: `1px solid ${border}`,
                      bgcolor: isDark
                        ? alpha("#fff", 0.02)
                        : alpha(ACCENT, 0.03),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <DescriptionOutlinedIcon
                        sx={{ fontSize: 18, color: ACCENT }}
                      />
                      <Typography sx={{ fontWeight: 800, fontSize: "0.92rem" }}>
                        {item.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: muted,
                        fontSize: "0.82rem",
                        lineHeight: 1.45,
                      }}
                    >
                      {item.body}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        {/* Referrals */}
        <Box
          sx={{
            mb: 5,
            p: { xs: 2.25, sm: 3 },
            borderRadius: 3,
            border: `1px solid ${alpha(ACCENT, 0.35)}`,
            bgcolor: surface,
            background: isDark
              ? `linear-gradient(120deg, ${alpha(ACCENT, 0.18)}, transparent 60%)`
              : `linear-gradient(120deg, ${alpha(ACCENT, 0.08)}, transparent 60%)`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(ACCENT, isDark ? 0.25 : 0.12),
                color: ACCENT,
                flexShrink: 0,
              }}
            >
              <GroupsOutlinedIcon />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: "1.2rem",
                  letterSpacing: "-0.02em",
                  mb: 0.5,
                }}
              >
                Referrals when you’re interview-ready
              </Typography>
              <Typography
                sx={{
                  color: muted,
                  fontSize: "0.92rem",
                  lineHeight: 1.55,
                  maxWidth: 640,
                }}
              >
                Guaranteed introductions: <strong>2–3 FAANG</strong> referrals
                and <strong>5–6 product-based</strong> company referrals — after
                you’ve completed the planner milestones and mock bar. We open
                doors; you still have to clear the loop.
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${alpha("#d97706", 0.35)}`,
              bgcolor: alpha("#d97706", isDark ? 0.12 : 0.08),
            }}
          >
            <WarningAmberOutlinedIcon
              sx={{ color: "#d97706", fontSize: 20, mt: 0.1, flexShrink: 0 }}
            />
            <Typography
              variant="body2"
              sx={{ color: muted, fontSize: "0.82rem", lineHeight: 1.5 }}
            >
              <Box
                component="span"
                sx={{ fontWeight: 800, color: isDark ? "#e6edf3" : "#0f2a2b" }}
              >
                Important:{" "}
              </Box>
              Referrals are guaranteed — <strong>job placements are not</strong>
              . Offers depend on your performance in interviews, company hiring
              needs, and timing. No placement promise is made or implied.
            </Typography>
          </Box>
        </Box>

        {/* Pricing */}
        <Box id="pricing" sx={{ mb: 5, scrollMarginTop: 88 }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: "1.35rem", sm: "1.6rem" },
              letterSpacing: "-0.03em",
              mb: 0.75,
            }}
          >
            Mentorship plans
          </Typography>
          <Typography
            sx={{ color: muted, mb: 2.5, maxWidth: 520, fontSize: "0.95rem" }}
          >
            Commit monthly, or lock the 6–7 month planner at{" "}
            {formatInr(MENTORSHIP_SIX_MONTH_PRICE)} and save.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: "100%",
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 3,
                  border: `1px solid ${border}`,
                  bgcolor: surface,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: muted,
                    mb: 1,
                  }}
                >
                  Monthly
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: "2.1rem",
                    letterSpacing: "-0.04em",
                    mb: 0.5,
                  }}
                >
                  {formatInr(MENTORSHIP_MONTHLY_PRICE)}
                  <Box
                    component="span"
                    sx={{ fontSize: "1rem", fontWeight: 700, color: muted }}
                  >
                    /mo
                  </Box>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: muted, mb: 2, lineHeight: 1.5 }}
                >
                  Flexible month-to-month mentorship with daily connect and full
                  interview prep coverage.
                </Typography>
                <Box sx={{ flex: 1, mb: 2 }}>
                  {[
                    "DSA + System Design + CS Fundamentals",
                    "Daily connect & accountability",
                    "Session follow-ups every week",
                    "Mock interviews with written feedback",
                  ].map((t) => (
                    <Box
                      key={t}
                      sx={{
                        display: "flex",
                        gap: 1,
                        mb: 0.85,
                        alignItems: "flex-start",
                      }}
                    >
                      <CheckCircleIcon
                        sx={{ fontSize: 16, color: ACCENT, mt: 0.2 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.85rem", color: muted }}
                      >
                        {t}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => openCheckout("monthly")}
                  sx={{
                    textTransform: "none",
                    fontWeight: 800,
                    borderColor: ACCENT,
                    color: ACCENT,
                    py: 1.15,
                  }}
                >
                  Enroll monthly
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: "100%",
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 3,
                  border: `2px solid ${ACCENT}`,
                  bgcolor: surface,
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  background: isDark
                    ? `linear-gradient(160deg, ${alpha(ACCENT, 0.22)}, ${surface} 45%)`
                    : `linear-gradient(160deg, ${alpha(ACCENT, 0.1)}, #fff 45%)`,
                }}
              >
                <Chip
                  size="small"
                  label="Best value · save ~₹12k"
                  sx={{
                    position: "absolute",
                    top: 14,
                    right: 14,
                    fontWeight: 800,
                    bgcolor: PINK,
                    color: "#fff",
                    height: 24,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: ACCENT,
                    mb: 1,
                  }}
                >
                  6–7 month plan
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: "2.1rem",
                    letterSpacing: "-0.04em",
                    mb: 0.5,
                  }}
                >
                  {formatInr(MENTORSHIP_SIX_MONTH_PRICE)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: muted, mb: 2, lineHeight: 1.5 }}
                >
                  Full planner: 4 months DSA → 2 months HLD → LLD + CS
                  fundamentals, plus resume prep and referrals.
                </Typography>
                <Box sx={{ flex: 1, mb: 2 }}>
                  {[
                    "4 mo DSA · 2 mo HLD · 15d LLD · 15d CS",
                    "Resume writing & interview storytelling",
                    "2–3 FAANG + 5–6 product referrals",
                    "Daily connect, follow-ups & mock loops",
                  ].map((t) => (
                    <Box
                      key={t}
                      sx={{
                        display: "flex",
                        gap: 1,
                        mb: 0.85,
                        alignItems: "flex-start",
                      }}
                    >
                      <CheckCircleIcon
                        sx={{ fontSize: 16, color: ACCENT, mt: 0.2 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.85rem", color: muted }}
                      >
                        {t}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => openCheckout("six")}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 800,
                    bgcolor: ACCENT,
                    py: 1.15,
                    "&:hover": { bgcolor: "#0f5c5e" },
                  }}
                >
                  Lock 6–7 month plan
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Guidance + mock session options */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
            mb: 5,
          }}
        >
          <Box
            id="guidance-call"
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: 3,
              border: `1px solid ${border}`,
              bgcolor: surface,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              scrollMarginTop: 88,
              background: isDark
                ? `linear-gradient(145deg, ${alpha(ACCENT, 0.16)}, transparent 60%)`
                : `linear-gradient(145deg, ${alpha(ACCENT, 0.08)}, transparent 60%)`,
            }}
          >
            <Box>
              <Chip
                size="small"
                label="Not ready for full mentorship?"
                sx={{
                  mb: 1.25,
                  fontWeight: 700,
                  bgcolor: alpha(ACCENT, isDark ? 0.25 : 0.12),
                  color: ACCENT,
                }}
              />
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: "1.25rem",
                  letterSpacing: "-0.03em",
                  mb: 0.75,
                }}
              >
                1–2 hour guidance call · {formatInr(GUIDANCE_SESSION_PRICE)}
              </Typography>
              <Typography
                sx={{ color: muted, fontSize: "0.9rem", lineHeight: 1.55 }}
              >
                Need a roadmap, resume review, mock feedback, or a straight talk
                on what to study next? Book a focused 1:1 discussion — no long
                commitment.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={() => openCheckout("guidance")}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                bgcolor: ACCENT,
                alignSelf: { xs: "stretch", sm: "flex-start" },
                px: 2.5,
                mt: "auto",
                "&:hover": { bgcolor: "#0f5c5e" },
              }}
            >
              Book for {formatInr(GUIDANCE_SESSION_PRICE)}
            </Button>
          </Box>

          <Box
            id="mock-interview"
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: 3,
              border: `1px solid ${border}`,
              bgcolor: surface,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              scrollMarginTop: 88,
              background: isDark
                ? `linear-gradient(145deg, ${alpha(PINK, 0.14)}, transparent 60%)`
                : `linear-gradient(145deg, ${alpha(PINK, 0.07)}, transparent 60%)`,
            }}
          >
            <Box>
              <Chip
                size="small"
                label="Quick mock · no long commitment"
                sx={{
                  mb: 1.25,
                  fontWeight: 700,
                  bgcolor: alpha(PINK, isDark ? 0.2 : 0.1),
                  color: PINK,
                }}
              />
              {isPremiumUser && (
                <Chip
                  size="small"
                  label="Premium price"
                  sx={{
                    mb: 1.25,
                    ml: 0.75,
                    fontWeight: 700,
                    bgcolor: alpha("#d97706", isDark ? 0.22 : 0.12),
                    color: "#d97706",
                  }}
                />
              )}
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: "1.25rem",
                  letterSpacing: "-0.03em",
                  mb: 0.75,
                }}
              >
                Schedule a mock interview · {mockPriceLabel}
              </Typography>
              <Typography
                sx={{ color: muted, fontSize: "0.9rem", lineHeight: 1.55 }}
              >
                Pick a date and time for a focused mock — DSA, design, or
                behavioral. Pay{" "}
                {isPremiumUser ? (
                  <>
                    {formatInr(MOCK_INTERVIEW_PREMIUM_PRICE)} as a premium
                    member
                  </>
                ) : (
                  formatInr(MOCK_INTERVIEW_BOOKING_PRICE)
                )}{" "}
                and we’ll confirm your slot.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={() => openCheckout("mock")}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                bgcolor: PINK,
                alignSelf: { xs: "stretch", sm: "flex-start" },
                px: 2.5,
                mt: "auto",
                "&:hover": { bgcolor: "#c40048" },
              }}
            >
              Book for {formatInr(mockInterviewPrice)}
            </Button>
          </Box>
        </Box>

        {/* How it works */}
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: "1.35rem", sm: "1.6rem" },
              letterSpacing: "-0.03em",
              mb: 2.5,
            }}
          >
            How onboarding works
          </Typography>
          <Grid container spacing={1.5}>
            {[
              {
                icon: <PersonOutlineIcon sx={{ fontSize: 20 }} />,
                title: "Share your goal",
                body: "Target role, timeline, and where you’re stuck today.",
              },
              {
                icon: <CalendarMonthIcon sx={{ fontSize: 20 }} />,
                title: "Pick a plan",
                body: isPremiumUser
                  ? "₹1 mock (premium), ₹1,199 guidance, monthly, or 6-month deep prep."
                  : "₹99 mock, ₹1,199 guidance, monthly, or 6-month deep prep.",
              },
              {
                icon: <PaymentIcon sx={{ fontSize: 20 }} />,
                title: "Confirm securely",
                body: "Razorpay checkout — then we reach out to schedule.",
              },
              {
                icon: <RocketLaunchOutlinedIcon sx={{ fontSize: 20 }} />,
                title: "Daily connect starts",
                body: "Roadmap, follow-ups, and mocks until you’re offer-ready.",
              },
            ].map((step, i) => (
              <Grid item xs={12} sm={6} md={3} key={step.title}>
                <Box
                  sx={{
                    p: 2,
                    height: "100%",
                    borderRadius: 2,
                    border: `1px solid ${border}`,
                    bgcolor: surface,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        bgcolor: ACCENT,
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: "0.75rem",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Box sx={{ color: ACCENT, display: "flex" }}>
                      {step.icon}
                    </Box>
                  </Box>
                  <Typography
                    sx={{ fontWeight: 800, fontSize: "0.9rem", mb: 0.4 }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: muted, fontSize: "0.8rem", lineHeight: 1.4 }}
                  >
                    {step.body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <TestimonialsSection
          context="mentorship"
          limit={4}
          title="Mentorship that moves the needle"
          subtitle="Engineers who booked guidance, mocks, or the full mentorship plan — in their own words."
          sx={{ mb: 4 }}
        />

        <Box
          sx={{
            mb: 4,
            p: { xs: 2.25, sm: 2.75 },
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
            textAlign: { xs: "left", sm: "center" },
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.05rem",
              letterSpacing: "-0.02em",
              mb: 0.75,
            }}
          >
            Have a question?
          </Typography>
          <Typography
            sx={{
              color: muted,
              fontSize: "0.92rem",
              lineHeight: 1.55,
              mb: 1.25,
            }}
          >
            Unsure which plan fits, need a custom schedule, or anything else —
            email us and we’ll help.
          </Typography>
          <Typography
            component="a"
            href="mailto:updates@crafterhack.com?subject=Mentorship%20question"
            sx={{
              color: ACCENT,
              fontWeight: 800,
              fontSize: "0.95rem",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            updates@crafterhack.com
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2.5,
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography sx={{ color: muted, fontSize: "0.9rem" }}>
            Prefer self-paced first? Practice Track and Interview Prep stay free
            to explore.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              component={Link}
              to="/practice-track"
              sx={{ textTransform: "none", fontWeight: 700, color: ACCENT }}
            >
              Practice Track
            </Button>
            <Button
              component={Link}
              to="/interview-prep"
              sx={{ textTransform: "none", fontWeight: 700, color: ACCENT }}
            >
              Interview Prep
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Checkout modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            width: { xs: "94%", sm: needsSlot ? 820 : 480 },
            maxWidth: "100vw",
            bgcolor: isDark ? "#0f1419" : "#f7f9fb",
            borderRadius: 3,
            border: `1px solid ${border}`,
            boxShadow: isDark
              ? "0 24px 64px rgba(0,0,0,0.55)"
              : "0 24px 64px rgba(15,60,70,0.18)",
            margin: "auto",
            marginTop: { xs: "2%", sm: "4%" },
            position: "relative",
            maxHeight: { xs: "96dvh", sm: "92vh" },
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 2.75 },
              pt: 2.25,
              pb: 2,
              borderBottom: `1px solid ${border}`,
              background: isDark
                ? `linear-gradient(135deg, ${alpha(ACCENT, 0.22)} 0%, #161b22 60%)`
                : `linear-gradient(135deg, ${alpha(ACCENT, 0.12)} 0%, #ffffff 60%)`,
              position: "relative",
              flexShrink: 0,
            }}
          >
            <IconButton
              onClick={() => setModalOpen(false)}
              sx={{ position: "absolute", top: 10, right: 10 }}
              aria-label="Close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Chip
              size="small"
              label={planLabel}
              sx={{
                mb: 1,
                fontWeight: 700,
                bgcolor: alpha(ACCENT, isDark ? 0.25 : 0.12),
                color: ACCENT,
              }}
            />
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.2rem", sm: "1.35rem" },
                letterSpacing: "-0.03em",
                pr: 5,
                mb: 0.5,
              }}
            >
              Confirm · {formatInr(amount)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: muted, maxWidth: 480, lineHeight: 1.45 }}
            >
              {isGuidance
                ? "Pick a slot for your 1–2 hour guidance call."
                : isMock
                  ? "Pick a date and time for your mock interview."
                  : "Share details — we’ll schedule your onboarding connect after payment."}
            </Typography>
          </Box>

          <Box
            sx={{
              px: { xs: 2, sm: 2.75 },
              py: { xs: 2, sm: 2.5 },
              overflowY: "auto",
              flex: 1,
            }}
          >
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={needsSlot ? 5 : 12}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: `1px solid ${border}`,
                    bgcolor: surface,
                    height: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.72rem",
                      letterSpacing: "0.08em",
                      color: muted,
                      mb: 1.5,
                    }}
                  >
                    YOUR DETAILS
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {isGuidance && (
                      <FormControl fullWidth>
                        <TextField
                          select
                          label="Focus"
                          value={interviewType}
                          onChange={(e) => setInterviewType(e.target.value)}
                          size="small"
                        >
                          <MenuItem value="Guidance · DSA / Design / CS">
                            DSA / Design / CS
                          </MenuItem>
                          <MenuItem value="Guidance · Mock interview">
                            Mock interview
                          </MenuItem>
                          <MenuItem value="Guidance · Resume & roadmap">
                            Resume & roadmap
                          </MenuItem>
                          <MenuItem value="Guidance · Offer strategy">
                            Offer strategy
                          </MenuItem>
                        </TextField>
                      </FormControl>
                    )}
                    {isMock && (
                      <FormControl fullWidth>
                        <TextField
                          select
                          label="Focus"
                          value={interviewType}
                          onChange={(e) => setInterviewType(e.target.value)}
                          size="small"
                        >
                          <MenuItem value="Mock Interview · DSA">DSA</MenuItem>
                          <MenuItem value="Mock Interview · System Design">
                            System Design
                          </MenuItem>
                          <MenuItem value="Mock Interview · LLD">LLD</MenuItem>
                          <MenuItem value="Mock Interview · Behavioral">
                            Behavioral
                          </MenuItem>
                          <MenuItem value="Mock Interview · Mixed">
                            Mixed / Full loop
                          </MenuItem>
                        </TextField>
                      </FormControl>
                    )}
                    <TextField
                      fullWidth
                      label="Full name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      size="small"
                    />
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      size="small"
                    />
                    <TextField
                      fullWidth
                      label="WhatsApp / contact"
                      value={contactNumber}
                      onChange={(e) => setContactNumber(e.target.value)}
                      required
                      size="small"
                    />
                  </Box>
                </Box>
              </Grid>

              {needsSlot && (
                <Grid item xs={12} md={7}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      border: `1px solid ${border}`,
                      bgcolor: surface,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.72rem",
                        letterSpacing: "0.08em",
                        color: muted,
                        mb: 1.25,
                      }}
                    >
                      DATE & TIME
                    </Typography>
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${border}`,
                        bgcolor: isDark ? "#0d1117" : alpha(ACCENT, 0.03),
                        mb: 2,
                        "& .MuiPickersDay-root.Mui-selected": {
                          bgcolor: `${ACCENT} !important`,
                        },
                      }}
                    >
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateCalendar
                          value={date}
                          onChange={(d) => setDate(d)}
                          disablePast
                          sx={{
                            width: "100%",
                            maxHeight: { xs: 240, sm: 300 },
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.75,
                        mb: 2,
                      }}
                    >
                      {interviewTimeSlots.map((slot) => {
                        const selected = timeSlot === slot;
                        return (
                          <Chip
                            key={slot}
                            label={slot}
                            clickable
                            onClick={() => setTimeSlot(slot)}
                            sx={{
                              fontWeight: selected ? 700 : 550,
                              fontSize: "0.75rem",
                              height: 32,
                              borderRadius: 1.5,
                              bgcolor: selected
                                ? ACCENT
                                : isDark
                                  ? alpha("#fff", 0.05)
                                  : alpha(ACCENT, 0.06),
                              color: selected
                                ? "#fff"
                                : isDark
                                  ? "#c9d1d9"
                                  : "#0f2a2b",
                              border: `1px solid ${selected ? ACCENT : border}`,
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>

            <Button
              size="large"
              variant="contained"
              onClick={handleBooking}
              disabled={!canPay}
              startIcon={!loading ? <PaymentIcon /> : undefined}
              sx={{
                width: "100%",
                mt: 2.5,
                textTransform: "none",
                fontWeight: 800,
                py: 1.35,
                borderRadius: 2,
                bgcolor: ACCENT,
                "&:hover": { bgcolor: "#0f5c5e" },
              }}
            >
              {loading ? "Processing…" : `Pay ${formatInr(amount)} to confirm`}
            </Button>
            <Typography
              variant="caption"
              sx={{
                color: muted,
                textAlign: "center",
                display: "block",
                mt: 1,
              }}
            >
              Secure checkout via Razorpay
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: muted,
                textAlign: "center",
                display: "block",
                mt: 0.5,
              }}
            >
              Have any questions? Feel free to reach out{" "}
              <MuiLink
                href="mailto:updates@crafterhack.com"
                sx={{ color: ACCENT, fontWeight: 600 }}
              >
                updates@crafterhack.com
              </MuiLink>
            </Typography>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
      >
        <Box
          sx={{
            width: { xs: "92%", sm: 450 },
            p: 3,
            bgcolor: isDark ? "#161b22" : "background.paper",
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.12)",
            margin: "auto",
            marginTop: "10%",
            position: "relative",
            textAlign: "center",
          }}
        >
          <IconButton
            onClick={() => setConfirmationModalOpen(false)}
            sx={{ position: "absolute", top: 12, right: 12 }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
            <Box
              sx={{
                bgcolor: alpha("#1a7f37", 0.15),
                color: "#1a7f37",
                p: 1.75,
                borderRadius: "50%",
                display: "inline-flex",
              }}
            >
              <CheckCircleIcon fontSize="large" />
            </Box>
          </Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, mb: 1, letterSpacing: "-0.02em" }}
          >
            You’re in
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Confirmation email on the way — we’ll reach out on WhatsApp/email to
            schedule your next connect.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/practice-track")}
            sx={{
              width: "100%",
              textTransform: "none",
              fontWeight: 700,
              bgcolor: ACCENT,
              py: 1.25,
              mb: 1.25,
              "&:hover": { bgcolor: "#0f5c5e" },
            }}
          >
            Open Practice Track
          </Button>
          <Button
            variant="outlined"
            onClick={() => setConfirmationModalOpen(false)}
            sx={{
              width: "100%",
              textTransform: "none",
              fontWeight: 650,
              borderColor: border,
              color: isDark ? "#e6edf3" : "#0f2a2b",
              py: 1.1,
            }}
          >
            Close
          </Button>
        </Box>
      </Modal>

      <Modal
        open={failedPaymentModalOpen}
        onClose={() => setFailedPaymentModalOpen(false)}
      >
        <Box
          sx={{
            width: { xs: "92%", sm: 450 },
            p: 3,
            bgcolor: isDark ? "#161b22" : "background.paper",
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            margin: "auto",
            marginTop: "10%",
            position: "relative",
            textAlign: "center",
          }}
        >
          <IconButton
            onClick={() => setFailedPaymentModalOpen(false)}
            sx={{ position: "absolute", top: 12, right: 12 }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
            <Box
              sx={{
                bgcolor: alpha("#cf222e", 0.12),
                color: "#cf222e",
                p: 1.75,
                borderRadius: "50%",
                display: "inline-flex",
              }}
            >
              <ErrorIcon fontSize="large" />
            </Box>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
            Payment failed
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Try again — if it keeps failing, ping support with your email.
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              setFailedPaymentModalOpen(false);
              setModalOpen(true);
            }}
            sx={{
              width: "100%",
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "#cf222e",
              py: 1.25,
              mb: 1.25,
              "&:hover": { bgcolor: "#a40e26" },
            }}
          >
            Retry payment
          </Button>
          <Button
            variant="outlined"
            onClick={() => setFailedPaymentModalOpen(false)}
            sx={{
              width: "100%",
              textTransform: "none",
              borderColor: border,
              color: isDark ? "#e6edf3" : "#0f2a2b",
            }}
          >
            Close
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default BookingPage;
